import React, { useState, useEffect } from 'react';
import { Users, Shield, Key, UserCheck, Plus, Search, Edit, Trash2 } from 'lucide-react';

interface Permission {
  id: string;
  name: string;
  description: string;
  resource: string;
  action: string;
  category: 'System' | 'User' | 'Data' | 'Device' | 'Report';
  level: 'Basic' | 'Advanced' | 'Admin';
}

interface Role {
  id: string;
  name: string;
  description: string;
  permissions: string[];
  userCount: number;
  isSystem: boolean;
  createdAt: string;
  updatedAt: string;
}

interface UserPermission {
  id: string;
  userId: string;
  userName: string;
  userEmail: string;
  roleId: string;
  roleName: string;
  department: string;
  additionalPermissions: string[];
  restrictedPermissions: string[];
  lastAccess: string;
  status: 'Active' | 'Inactive' | 'Suspended';
}

const PermissionsManagement: React.FC = () => {
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [userPermissions, setUserPermissions] = useState<UserPermission[]>([]);
  const [activeTab, setActiveTab] = useState<'permissions' | 'roles' | 'users'>('permissions');
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [levelFilter, setLevelFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPermissions();
    fetchRoles();
    fetchUserPermissions();
  }, []);

  const fetchPermissions = async () => {
    try {
      const mockPermissions: Permission[] = [
        {
          id: '1',
          name: 'View Patients',
          description: 'View patient information and records',
          resource: 'patients',
          action: 'read',
          category: 'Data',
          level: 'Basic'
        },
        {
          id: '2',
          name: 'Edit Patients',
          description: 'Modify patient information and records',
          resource: 'patients',
          action: 'write',
          category: 'Data',
          level: 'Advanced'
        },
        {
          id: '3',
          name: 'Delete Patients',
          description: 'Delete patient records (restricted)',
          resource: 'patients',
          action: 'delete',
          category: 'Data',
          level: 'Admin'
        },
        {
          id: '4',
          name: 'Manage Users',
          description: 'Create, edit, and manage user accounts',
          resource: 'users',
          action: 'manage',
          category: 'User',
          level: 'Admin'
        },
        {
          id: '5',
          name: 'View Reports',
          description: 'Access and view system reports',
          resource: 'reports',
          action: 'read',
          category: 'Report',
          level: 'Basic'
        },
        {
          id: '6',
          name: 'Generate Reports',
          description: 'Create and generate custom reports',
          resource: 'reports',
          action: 'create',
          category: 'Report',
          level: 'Advanced'
        },
        {
          id: '7',
          name: 'Control Devices',
          description: 'Control and configure medical devices',
          resource: 'devices',
          action: 'control',
          category: 'Device',
          level: 'Advanced'
        },
        {
          id: '8',
          name: 'System Configuration',
          description: 'Access system settings and configuration',
          resource: 'system',
          action: 'configure',
          category: 'System',
          level: 'Admin'
        }
      ];
      setPermissions(mockPermissions);
    } catch (error) {
      console.error('Error fetching permissions:', error);
    }
  };

  const fetchRoles = async () => {
    try {
      const mockRoles: Role[] = [
        {
          id: '1',
          name: 'Radiologist',
          description: 'Medical professional who interprets medical images',
          permissions: ['1', '2', '5', '6'],
          userCount: 15,
          isSystem: true,
          createdAt: '2024-01-15',
          updatedAt: '2024-01-15'
        },
        {
          id: '2',
          name: 'Technologist',
          description: 'Medical imaging technologist',
          permissions: ['1', '5', '7'],
          userCount: 25,
          isSystem: true,
          createdAt: '2024-01-15',
          updatedAt: '2024-02-10'
        },
        {
          id: '3',
          name: 'Administrator',
          description: 'System administrator with full access',
          permissions: ['1', '2', '3', '4', '5', '6', '7', '8'],
          userCount: 5,
          isSystem: true,
          createdAt: '2024-01-15',
          updatedAt: '2024-01-15'
        },
        {
          id: '4',
          name: 'Nurse',
          description: 'Nursing staff with limited access',
          permissions: ['1', '5'],
          userCount: 30,
          isSystem: false,
          createdAt: '2024-01-20',
          updatedAt: '2024-02-15'
        },
        {
          id: '5',
          name: 'Resident',
          description: 'Medical resident with supervised access',
          permissions: ['1', '5'],
          userCount: 12,
          isSystem: false,
          createdAt: '2024-02-01',
          updatedAt: '2024-02-20'
        }
      ];
      setRoles(mockRoles);
    } catch (error) {
      console.error('Error fetching roles:', error);
    }
  };

  const fetchUserPermissions = async () => {
    try {
      const mockUserPermissions: UserPermission[] = [
        {
          id: '1',
          userId: 'u1',
          userName: 'Dr. Sarah Johnson',
          userEmail: 'sarah.johnson@hospital.com',
          roleId: '1',
          roleName: 'Radiologist',
          department: 'Radiology',
          additionalPermissions: ['7'],
          restrictedPermissions: [],
          lastAccess: '2024-01-29T14:30:00Z',
          status: 'Active'
        },
        {
          id: '2',
          userId: 'u2',
          userName: 'Mike Chen',
          userEmail: 'mike.chen@hospital.com',
          roleId: '2',
          roleName: 'Technologist',
          department: 'Imaging',
          additionalPermissions: [],
          restrictedPermissions: ['7'],
          lastAccess: '2024-01-29T16:45:00Z',
          status: 'Active'
        },
        {
          id: '3',
          userId: 'u3',
          userName: 'Admin User',
          userEmail: 'admin@hospital.com',
          roleId: '3',
          roleName: 'Administrator',
          department: 'IT',
          additionalPermissions: [],
          restrictedPermissions: [],
          lastAccess: '2024-01-29T18:20:00Z',
          status: 'Active'
        },
        {
          id: '4',
          userId: 'u4',
          userName: 'Jane Smith',
          userEmail: 'jane.smith@hospital.com',
          roleId: '4',
          roleName: 'Nurse',
          department: 'Emergency',
          additionalPermissions: [],
          restrictedPermissions: [],
          lastAccess: '2024-01-28T10:15:00Z',
          status: 'Inactive'
        },
        {
          id: '5',
          userId: 'u5',
          userName: 'Dr. Robert Lee',
          userEmail: 'robert.lee@hospital.com',
          roleId: '5',
          roleName: 'Resident',
          department: 'Radiology',
          additionalPermissions: [],
          restrictedPermissions: [],
          lastAccess: '2024-01-29T12:30:00Z',
          status: 'Active'
        }
      ];
      setUserPermissions(mockUserPermissions);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching user permissions:', error);
      setLoading(false);
    }
  };


  const handleEditItem = (item: any) => {
    setSelectedItem(item);
    setShowEditModal(true);
  };

  const handleUpdateItem = (updatedItem: any) => {
    if (activeTab === 'permissions') {
      setPermissions(permissions.map(p => p.id === updatedItem.id ? updatedItem : p));
    } else if (activeTab === 'roles') {
      setRoles(roles.map(r => r.id === updatedItem.id ? updatedItem : r));
    } else if (activeTab === 'users') {
      setUserPermissions(userPermissions.map(u => u.id === updatedItem.id ? updatedItem : u));
    }
    setShowEditModal(false);
    setSelectedItem(null);
  };

  const handleDeleteItem = (id: string) => {
    if (window.confirm('Are you sure you want to delete this item?')) {
      if (activeTab === 'permissions') {
        setPermissions(permissions.filter(p => p.id !== id));
      } else if (activeTab === 'roles') {
        setRoles(roles.filter(r => r.id !== id));
      } else if (activeTab === 'users') {
        setUserPermissions(userPermissions.filter(u => u.id !== id));
      }
    }
  };

  const getPermissionName = (permissionId: string) => {
    const permission = permissions.find(p => p.id === permissionId);
    return permission ? permission.name : permissionId;
  };

  const filteredPermissions = permissions.filter(permission => {
    const matchesSearch = permission.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         permission.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = categoryFilter === 'all' || permission.category === categoryFilter;
    const matchesLevel = levelFilter === 'all' || permission.level === levelFilter;
    return matchesSearch && matchesCategory && matchesLevel;
  });

  const filteredRoles = roles.filter(role => {
    const matchesSearch = role.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         role.description.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  const filteredUserPermissions = userPermissions.filter(userPerm => {
    const matchesSearch = userPerm.userName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         userPerm.userEmail.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         userPerm.roleName.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || userPerm.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-800 mb-2">Permissions Management</h1>
        <p className="text-gray-600">Manage system permissions, roles, and user access controls</p>
      </div>

      {/* Tabs */}
      <div className="flex space-x-4 mb-6">
        <button
          onClick={() => setActiveTab('permissions')}
          className={`px-4 py-2 rounded-lg font-medium transition-colors ${
            activeTab === 'permissions'
              ? 'bg-blue-100 text-blue-700 border border-blue-200'
              : 'text-gray-600 hover:text-gray-800'
          }`}
        >
          <Key className="w-4 h-4 inline mr-2" />
          Permissions
        </button>
        <button
          onClick={() => setActiveTab('roles')}
          className={`px-4 py-2 rounded-lg font-medium transition-colors ${
            activeTab === 'roles'
              ? 'bg-blue-100 text-blue-700 border border-blue-200'
              : 'text-gray-600 hover:text-gray-800'
          }`}
        >
          <Shield className="w-4 h-4 inline mr-2" />
          Roles
        </button>
        <button
          onClick={() => setActiveTab('users')}
          className={`px-4 py-2 rounded-lg font-medium transition-colors ${
            activeTab === 'users'
              ? 'bg-blue-100 text-blue-700 border border-blue-200'
              : 'text-gray-600 hover:text-gray-800'
          }`}
        >
          <Users className="w-4 h-4 inline mr-2" />
          User Permissions
        </button>
      </div>

      {/* Search and Filters */}
      <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex-1 min-w-64">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder={`Search ${activeTab}...`}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          {activeTab === 'permissions' && (
            <>
              <select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">All Categories</option>
                <option value="System">System</option>
                <option value="User">User</option>
                <option value="Data">Data</option>
                <option value="Device">Device</option>
                <option value="Report">Report</option>
              </select>

              <select
                value={levelFilter}
                onChange={(e) => setLevelFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">All Levels</option>
                <option value="Basic">Basic</option>
                <option value="Advanced">Advanced</option>
                <option value="Admin">Admin</option>
              </select>
            </>
          )}

          {activeTab === 'users' && (
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">All Status</option>
              <option value="Active">Active</option>
              <option value="Inactive">Inactive</option>
              <option value="Suspended">Suspended</option>
            </select>
          )}

          <button
            onClick={() => setShowCreateModal(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center"
          >
            <Plus className="w-4 h-4 mr-2" />
            Create {activeTab === 'permissions' ? 'Permission' : activeTab === 'roles' ? 'Role' : 'User Assignment'}
          </button>
        </div>
      </div>

      {/* Content based on active tab */}
      {activeTab === 'permissions' && (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Resource</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Level</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredPermissions.map((permission) => (
                  <tr key={permission.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{permission.name}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-700">{permission.description}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                        {permission.resource}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        {permission.action}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        permission.category === 'System' ? 'bg-red-100 text-red-800' :
                        permission.category === 'User' ? 'bg-green-100 text-green-800' :
                        permission.category === 'Data' ? 'bg-yellow-100 text-yellow-800' :
                        permission.category === 'Device' ? 'bg-purple-100 text-purple-800' :
                        'bg-indigo-100 text-indigo-800'
                      }`}>
                        {permission.category}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        permission.level === 'Admin' ? 'bg-red-100 text-red-800' :
                        permission.level === 'Advanced' ? 'bg-orange-100 text-orange-800' :
                        'bg-green-100 text-green-800'
                      }`}>
                        {permission.level}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => handleEditItem(permission)}
                        className="text-blue-600 hover:text-blue-900 mr-3"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteItem(permission.id)}
                        className="text-red-600 hover:text-red-900"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === 'roles' && (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Permissions</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Users</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredRoles.map((role) => (
                  <tr key={role.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <Shield className="w-5 h-5 text-gray-400 mr-2" />
                        <div className="text-sm font-medium text-gray-900">{role.name}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-700">{role.description}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-wrap gap-1">
                        {role.permissions.slice(0, 3).map((permId) => (
                          <span
                            key={permId}
                            className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-blue-100 text-blue-800"
                          >
                            {getPermissionName(permId)}
                          </span>
                        ))}
                        {role.permissions.length > 3 && (
                          <span className="text-xs text-gray-500">+{role.permissions.length - 3} more</span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{role.userCount}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        role.isSystem ? 'bg-gray-100 text-gray-800' : 'bg-green-100 text-green-800'
                      }`}>
                        {role.isSystem ? 'System' : 'Custom'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => handleEditItem(role)}
                        className="text-blue-600 hover:text-blue-900 mr-3"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      {!role.isSystem && (
                        <button
                          onClick={() => handleDeleteItem(role.id)}
                          className="text-red-600 hover:text-red-900"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === 'users' && (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Department</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Additional Permissions</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Access</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredUserPermissions.map((userPerm) => (
                  <tr key={userPerm.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <UserCheck className="w-5 h-5 text-gray-400 mr-2" />
                        <div>
                          <div className="text-sm font-medium text-gray-900">{userPerm.userName}</div>
                          <div className="text-sm text-gray-500">{userPerm.userEmail}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        {userPerm.roleName}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{userPerm.department}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-2">
                        {userPerm.additionalPermissions.length > 0 && (
                          <span className="text-xs text-green-600">+{userPerm.additionalPermissions.length}</span>
                        )}
                        {userPerm.restrictedPermissions.length > 0 && (
                          <span className="text-xs text-red-600">-{userPerm.restrictedPermissions.length}</span>
                        )}
                        {userPerm.additionalPermissions.length === 0 && userPerm.restrictedPermissions.length === 0 && (
                          <span className="text-xs text-gray-500">Standard</span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        userPerm.status === 'Active' ? 'bg-green-100 text-green-800' :
                        userPerm.status === 'Inactive' ? 'bg-gray-100 text-gray-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {userPerm.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {new Date(userPerm.lastAccess).toLocaleDateString()}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => handleEditItem(userPerm)}
                        className="text-blue-600 hover:text-blue-900 mr-3"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteItem(userPerm.id)}
                        className="text-red-600 hover:text-red-900"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Create/Edit Modals would go here - simplified for brevity */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-medium mb-4">
              Create New {activeTab === 'permissions' ? 'Permission' : activeTab === 'roles' ? 'Role' : 'User Assignment'}
            </h3>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowCreateModal(false)}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={() => setShowCreateModal(false)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Create
              </button>
            </div>
          </div>
        </div>
      )}

      {showEditModal && selectedItem && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-medium mb-4">
              Edit {activeTab === 'permissions' ? 'Permission' : activeTab === 'roles' ? 'Role' : 'User Assignment'}
            </h3>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setSelectedItem(null);
                }}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={() => handleUpdateItem(selectedItem)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PermissionsManagement;